/* eslint-disable complexity */
import {
  CustomLayer,
  Datum,
  Layer,
  PointTooltipProps,
  ResponsiveLine,
  Serie,
} from '@nivo/line';
import { AverageTrendSeriesId } from 'libs/end-to-end/src/progress';
import { extractLineGroupFromSeriesId } from 'libs/end-to-end/src/progress';
import { buildLineGroupSeriesId } from 'libs/end-to-end/src/progress';
import { ControlGroupSeriesId } from 'libs/end-to-end/src/progress';
import { createColorPaletteFromBuckets } from 'libs/end-to-end/src/progress';
import { TimeSeriesLineChartSerializableProps } from 'libs/end-to-end/src/progress';
import React, { useCallback, useMemo, useReducer } from 'react';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { tryParseISO } from 'libs/end-to-end/src/dates';
import { Color } from 'libs/end-to-end/src/types/branding/color';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { formatNumberForDisplayInUnits } from 'libs/end-to-end/src/number-formatting';
import { formatNumber } from 'libs/end-to-end/src/number-formatting';
import { RESPONSIVE_LINE_MARGIN } from 'apps/sigma2-ui/src/lib/charts/constants';
import { RESPONSIVE_LINE_Y_AXIS_LABEL_OFFSET } from 'apps/sigma2-ui/src/lib/charts/constants';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { ProgressPageChartTooltipPure } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressPageChartTooltip';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { humanizeDateRange } from 'libs/end-to-end/src/humanize-date-range';
import { formatDateToYYYYMMDD } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { startOfMonth } from 'date-fns';
import { startOfYear } from 'date-fns';
import { startOfWeek } from 'date-fns';
import { startOfDay } from 'date-fns';
import { TimeSeriesTrendsResultBucket } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsTypes';
import { NumericDisplayUnit } from 'libs/end-to-end/src/metrics';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { TreatmentArrowLayer } from 'apps/sigma2-ui/src/pages/ProgressPage/TreatmentLogic';
import { TreatmentGradientLayer } from 'apps/sigma2-ui/src/pages/ProgressPage/TreatmentLogic';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { ChartLegendButton } from 'apps/sigma2-ui/src/pages/ProgressPage/ChartLegendButton';
import { ScaleValue } from '@nivo/scales';
import { NivoSafeTooltipWrapper } from 'apps/sigma2-ui/src/lib/hooks/useTouchableTooltipCollection';

const TimeSeriesLineLayer: CustomLayer = (props) => {
  const { series, lineGenerator } = props;
  return (
    <g>
      {series.map((serie) => {
        const lineStyle =
          serie.id === AverageTrendSeriesId ? 'dashed' : 'solid';
        const strokeDasharray = lineStyle === 'dashed' ? '4,3' : undefined;

        const pathD = lineGenerator(
          serie.data.map((d) => ({
            x: d.position.x,
            y: d.position.y,
          })),
        );

        return (
          <path
            key={serie.id}
            d={pathD ?? undefined}
            fill="none"
            stroke={serie.color}
            strokeWidth={2.5}
            strokeDasharray={strokeDasharray}
            opacity={1}
          />
        );
      })}
    </g>
  );
};

interface TimeSeries extends Serie {
  tooltips: React.ReactNode[];
  label: string;
  color: Color;
}

const getAverageTrendSeriesValue = ({
  bucket,
  useCountValue,
}: {
  bucket: TimeSeriesTrendsResultBucket;
  useCountValue: boolean;
}): number | null => {
  if (useCountValue) {
    return bucket.fittedCountValue ?? null;
  }

  return bucket.fittedValue ?? null;
};

const getObservedSeriesValue = ({
  element,
  useCountValue,
}: {
  element: TimeSeriesTrendsResultBucket['elements'][number] | undefined;
  useCountValue: boolean;
}): number | null => {
  if (!element) {
    return null;
  }

  if (useCountValue) {
    return element.numerator ?? null;
  }

  return element.percentage ?? null;
};

const formatTooltipCount = (value: number | null): string | undefined =>
  value === null ? undefined : formatNumber(value);

const formatTooltipMetricValue = ({
  value,
  units,
}: {
  value: number;
  units: NumericDisplayUnit;
}): string => {
  if (units === 'count') {
    return formatNumber(value);
  }

  return formatNumberForDisplayInUnits({
    value,
    units,
    isPercentageAlreadyMultipliedBy100: false,
  });
};

export const TimeSeriesLineChart: React.FC<
  TimeSeriesLineChartSerializableProps
> = ({
  data,
  metricDefinition,
  units,
  isMetricBoolean,
  treatmentGroup,
  viewBy,
  isPrintMode,
  config,
  viewConfiguration,
}) => {
  const language = useLanguage();
  const useCountTrendValue = viewConfiguration.overallDisplayMode === 'count';

  const canonicalTime = useCallback(
    (d: Date) => {
      switch (viewBy) {
        case 'day':
          return startOfDay(d).getTime();
        case 'week':
          return startOfWeek(d).getTime();
        case 'month':
          return startOfMonth(d).getTime();
        case 'year':
          return startOfYear(d).getTime();
        default:
          throw guardExhaustive(viewBy);
      }
    },
    [viewBy],
  );
  const { avgTrendData, ctlGroupData, lineGroups, tickLabelByKey, tickValues } =
    useMemo(() => {
      const avgTrendData: Datum[] = [];
      const ctlGroupData: Datum[] = [];
      const lineGroups: Record<string, Datum[]> = {};
      const tickLabelByKey = new Map<number, string>();
      const tickValues: Date[] = [];

      for (const bucket of data.buckets) {
        const start = tryParseISO(bucket.start);
        const end = tryParseISO(bucket.end);

        if (!start || !end) {
          throw new Error('Failed to parse date range for bucket');
        }

        const tickKey = canonicalTime(start);

        tickValues.push(new Date(tickKey));
        tickLabelByKey.set(
          tickKey,
          humanizeDateRange({
            start: formatDateToYYYYMMDD(start),
            end: formatDateToYYYYMMDD(end),
          }),
        );

        const averageTrendValue = getAverageTrendSeriesValue({
          bucket,
          useCountValue: useCountTrendValue,
        });

        if (averageTrendValue != null) {
          avgTrendData.push({
            x: start,
            y: averageTrendValue,
          });
        }

        if (bucket.controlValue != null) {
          ctlGroupData.push({
            x: start,
            y: bucket.controlValue,
          });
        }

        for (const element of bucket.elements) {
          // IMPORTANT: when in DiD mode, remove the series for any groups that are not the treatment group
          if (treatmentGroup && element.group !== treatmentGroup) {
            continue;
          }

          if (lineGroups[element.group] === undefined) {
            lineGroups[element.group] = [];
          }

          // When we are in the view mode for "count", we pass the numerator (which represents the count of the metric row) to be displayed.
          lineGroups[element.group].push({
            x: start,
            y:
              viewConfiguration.overallDisplayMode === 'count'
                ? element.numerator
                : element.percentage,
          });
        }
      }

      return {
        avgTrendData,
        ctlGroupData,
        lineGroups,
        tickValues,
        tickLabelByKey,
      };
    }, [
      canonicalTime,
      data.buckets,
      treatmentGroup,
      useCountTrendValue,
      viewConfiguration,
    ]);

  const colorPalette = useMemo(
    () => createColorPaletteFromBuckets(data.buckets),
    [data.buckets],
  );

  const getSeriesColor = useCallback(
    (seriesId: string): Color => colorPalette[seriesId],
    [colorPalette],
  );

  // Since we allow to switch to overall mode, this might not be the same as units, which represents the "metric" unit.
  const graphUnits = useMemo(() => {
    if (viewConfiguration.overallDisplayMode === 'count') {
      return 'count';
    }
    return units;
  }, [units, viewConfiguration]);

  const buildTooltips = useCallback(
    ({ seriesId }: { seriesId: string }): React.ReactNode[] => {
      const tooltips: React.ReactNode[] = [];

      for (
        let bucketIndex = 0;
        bucketIndex < data.buckets.length;
        bucketIndex++
      ) {
        const bucket = data.buckets[bucketIndex];
        const compareBucket = data.compareWithBuckets?.at(bucketIndex);
        // todo: bucket.start formatting to make it not just YYYY-MM-DD
        const lineGroup = extractLineGroupFromSeriesId(seriesId);

        if (seriesId === AverageTrendSeriesId) {
          const averageTrendValue = getAverageTrendSeriesValue({
            bucket,
            useCountValue: useCountTrendValue,
          });
          const compareAverageTrendValue =
            compareBucket == null
              ? null
              : getAverageTrendSeriesValue({
                  bucket: compareBucket,
                  useCountValue: useCountTrendValue,
                });

          tooltips.push(
            <TimeSeriesSeriesPointTooltip
              bucket={bucket}
              compareBucket={compareBucket}
              color={getSeriesColor(seriesId)}
              compareElementValue={compareAverageTrendValue}
              compareTotal={
                !compareBucket
                  ? null
                  : reduceAttributeByKey(compareBucket.elements, 'total')
              }
              elementTotal={reduceAttributeByKey(bucket.elements, 'total')}
              elementValue={averageTrendValue}
              units={graphUnits}
              metricDefinition={metricDefinition}
              isMetricBoolean={isMetricBoolean}
              displayLevel={language.timeSeriesTrendsLineChartSeriesLabel({
                seriesId,
                metricDefinition,
              })}
              elementNumerator={bucket.fittedCountValue ?? null}
              compareElementNumerator={
                compareBucket ? (compareBucket.fittedCountValue ?? null) : null
              }
            />,
          );
        } else if (seriesId === ControlGroupSeriesId) {
          const elements = bucket.elements.filter(
            (e) => e.group !== treatmentGroup,
          );
          const compareElements = compareBucket?.elements.filter(
            (e) => e.group !== treatmentGroup,
          );

          tooltips.push(
            <TimeSeriesSeriesPointTooltip
              bucket={bucket}
              compareBucket={compareBucket}
              color={getSeriesColor(seriesId)}
              compareElementValue={
                compareBucket && compareBucket.controlValue != null
                  ? compareBucket.controlValue
                  : null
              }
              compareTotal={
                !compareElements ? null : reduceTotals(compareElements)
              }
              elementTotal={reduceTotals(elements)}
              elementValue={bucket.controlValue || null}
              units={graphUnits}
              metricDefinition={metricDefinition}
              isMetricBoolean={isMetricBoolean}
              displayLevel={language.timeSeriesTrendsLineChartSeriesLabel({
                seriesId,
                metricDefinition,
              })}
              elementNumerator={reduceAttributeByKey(elements, 'numerator')}
              compareElementNumerator={
                compareElements
                  ? reduceAttributeByKey(compareElements, 'numerator')
                  : null
              }
            />,
          );
        } else if (lineGroup) {
          const element = bucket.elements.find((e) => e.group === lineGroup);
          const compareElement = compareBucket?.elements.find(
            (e) => e.group === lineGroup,
          );

          tooltips.push(
            <TimeSeriesSeriesPointTooltip
              bucket={bucket}
              compareBucket={compareBucket}
              color={getSeriesColor(seriesId)}
              compareElementValue={getObservedSeriesValue({
                element: compareElement,
                useCountValue: useCountTrendValue,
              })}
              compareTotal={
                compareElement && compareElement.total !== null
                  ? compareElement.total
                  : null
              }
              elementTotal={
                element && element.total !== null ? element.total : null
              }
              elementValue={getObservedSeriesValue({
                element,
                useCountValue: useCountTrendValue,
              })}
              units={graphUnits}
              metricDefinition={metricDefinition}
              isMetricBoolean={isMetricBoolean}
              displayLevel={language.timeSeriesTrendsLineChartSeriesLabel({
                seriesId,
                metricDefinition,
              })}
              elementNumerator={element?.numerator ?? null}
              compareElementNumerator={compareElement?.numerator ?? null}
            />,
          );
        } else {
          // log an error
          tooltips.push(<></>);
        }
      }
      return tooltips;
    },
    [
      data.buckets,
      data.compareWithBuckets,
      graphUnits,
      getSeriesColor,
      isMetricBoolean,
      language,
      metricDefinition,
      treatmentGroup,
      useCountTrendValue,
      viewConfiguration.overallDisplayMode,
    ],
  );

  const allSeries = useMemo((): TimeSeries[] => {
    const result: TimeSeries[] = [];

    if (avgTrendData.length > 0) {
      result.push({
        id: AverageTrendSeriesId,
        data: avgTrendData,
        tooltips: buildTooltips({
          seriesId: AverageTrendSeriesId,
        }),
        label: language.timeSeriesTrendsLineChartSeriesLabel({
          seriesId: AverageTrendSeriesId,
          metricDefinition,
        }),
        color: getSeriesColor(AverageTrendSeriesId),
      });
    }

    if (ctlGroupData.length > 0) {
      result.push({
        id: ControlGroupSeriesId,
        data: ctlGroupData,
        tooltips: buildTooltips({
          seriesId: ControlGroupSeriesId,
        }),
        label: language.timeSeriesTrendsLineChartSeriesLabel({
          seriesId: ControlGroupSeriesId,
          metricDefinition,
        }),
        color: getSeriesColor(ControlGroupSeriesId),
      });
    }

    for (const [key, val] of Object.entries(lineGroups)) {
      const lineGroupSeriesId = buildLineGroupSeriesId({
        group: key,
      });
      result.push({
        id: lineGroupSeriesId,
        data: val,
        tooltips: buildTooltips({
          seriesId: lineGroupSeriesId,
        }),
        label: language.timeSeriesTrendsLineChartSeriesLabel({
          seriesId: lineGroupSeriesId,
          metricDefinition,
        }),
        color: getSeriesColor(lineGroupSeriesId),
      });
    }

    return result;
  }, [
    avgTrendData,
    buildTooltips,
    ctlGroupData,
    getSeriesColor,
    language,
    lineGroups,
    metricDefinition,
  ]);

  type SeriesVisibilityAction =
    | { type: 'toggle'; key: string }
    | { type: 'reset' };

  const hiddenReducer = (
    state: Set<string>,
    action: SeriesVisibilityAction,
  ) => {
    const next = new Set(state);

    switch (action.type) {
      case 'toggle': {
        if (next.has(action.key)) {
          next.delete(action.key);
        } else {
          next.add(action.key);
        }
        return next;
      }
      case 'reset': {
        return new Set<string>();
      }
      default: {
        throw guardExhaustive(action);
      }
    }
  };

  const [hiddenSeries, dispatchSeriesVisibility] = useReducer(
    hiddenReducer,
    new Set<string>(),
  );

  const toggleSeriesVisibility = useCallback(
    (key: string) => dispatchSeriesVisibility({ type: 'toggle', key }),
    [],
  );

  const resetSeriesVisibility = useCallback(
    () =>
      dispatchSeriesVisibility({
        type: 'reset',
      }),
    [],
  );

  const isSeriesVisible = useCallback(
    (seriesId: string) => !hiddenSeries.has(seriesId),
    [hiddenSeries],
  );

  const visibleSeries = useMemo(
    () => allSeries.filter((s) => isSeriesVisible(String(s.id))),
    [allSeries, isSeriesVisible],
  );

  const { xMin, xMax, yMin, yMax } = useMemo(() => {
    let xMin: Date | undefined;
    let xMax: Date | undefined;
    let yMin: number | undefined;
    let yMax: number | undefined;

    for (const s of allSeries) {
      for (const p of s.data) {
        const x = p.x instanceof Date ? p.x : undefined;
        const y = typeof p.y === 'number' ? p.y : undefined;

        if (x) {
          if (!xMin || x < xMin) {
            xMin = x;
          }
          if (!xMax || x > xMax) {
            xMax = x;
          }
        }

        if (y !== undefined) {
          if (yMin === undefined || y < yMin) {
            yMin = y;
          }
          if (yMax === undefined || y > yMax) {
            yMax = y;
          }
        }
      }
    }

    return { xMin, xMax, yMin, yMax };
  }, [allSeries]);

  const getTickLabel = useCallback(
    (value: ScaleValue): string => {
      if (!(value instanceof Date)) {
        return '';
      }

      return tickLabelByKey.get(canonicalTime(value)) ?? '';
    },
    [canonicalTime, tickLabelByKey],
  );

  const thinTicks = <T,>(values: T[], maxTicks: number): T[] =>
    values.length <= maxTicks
      ? values
      : values.filter((_, i) => i % Math.ceil(values.length / maxTicks) === 0);

  const layers = useMemo((): Layer[] => {
    const layersArr: Layer[] = [
      'grid',
      'markers',
      'axes',
      'areas',
      'crosshair',
    ];

    if (config.showTreatmentGradient) {
      layersArr.push((layerProps) => (
        <TreatmentGradientLayer
          key="treatment-gradient"
          series={layerProps.series}
          xScale={layerProps.xScale}
          yScale={layerProps.yScale}
          config={config}
          innerHeight={layerProps.innerHeight}
          shadowBeforeTreatmentDate={data.shadowBeforeTreatmentDate ?? false}
        />
      ));
    }

    layersArr.push(
      (layerProps) => <TimeSeriesLineLayer {...layerProps} />,
      (layerProps) => (
        <TreatmentArrowLayer
          key="treatment-arrows"
          series={layerProps.series}
          xScale={layerProps.xScale}
          yScale={layerProps.yScale}
          config={config}
          innerHeight={layerProps.innerHeight}
        />
      ),
      'points',
      'mesh',
    );

    return layersArr;
  }, [config, data.shadowBeforeTreatmentDate]);

  const handleFormatValue = useCallback(
    (value: number) =>
      formatNumberForDisplayInUnits({
        units: graphUnits,
        isPercentageAlreadyMultipliedBy100: false,
        value,
      }),
    [graphUnits],
  );

  const handleSeriesColor = useCallback(
    (series: TimeSeries) => ConfigColors[series.color].Primary.raw,
    [],
  );

  const handleTooltip = useCallback(
    (props: PointTooltipProps) => {
      // Extract the index of the point in the series from the point ID which is formatted like: "serieId.indexInSeries"
      const indexInSeries = Number(
        props.point.id.slice(props.point.id.lastIndexOf('.') + 1),
      );

      const allTooltips: React.ReactNode[] = [];

      const pointDataY =
        typeof props.point.data.y === 'number' ? props.point.data.y : NaN;

      for (const seriesSingle of allSeries) {
        const seriesSinglePoint = seriesSingle.data[indexInSeries];
        const seriesSinglePointY =
          typeof seriesSinglePoint.y === 'number' ? seriesSinglePoint.y : NaN;

        if (Math.abs(seriesSinglePointY - pointDataY) < 0.001) {
          allTooltips.push(seriesSingle.tooltips[indexInSeries]);
        }
      }

      return <div className="flex flex-col gap-4">{allTooltips}</div>;
    },
    [allSeries],
  );

  const containerRef = React.useRef<HTMLDivElement>(null);

  return (
    <Spacing size={'2xl'} direction={'vertical'} className="w-full">
      <Box w="full" h="h-[400px]" ref={containerRef}>
        <ResponsiveLine
          animate
          axisBottom={{
            format: getTickLabel,
            legend: language.progressChartXAxisLabel(),
            legendPosition: 'middle',
            legendOffset: 43,
            tickValues: thinTicks(tickValues, 8),
            tickSize: 5,
            tickPadding: 5,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: metricDefinition.displayName,
            legendOffset: RESPONSIVE_LINE_Y_AXIS_LABEL_OFFSET,
            legendPosition: 'middle',
            truncateTickAt: 0,
            format: handleFormatValue,
          }}
          curve="linear"
          enablePointLabel
          enableTouchCrosshair
          margin={RESPONSIVE_LINE_MARGIN}
          pointBorderColor={{
            from: 'serieColor',
            modifiers: [
              ['darker', 0.8],
              ['opacity', 0.5],
            ],
          }}
          pointColor={'none'}
          pointBorderWidth={2}
          pointSize={6}
          useMesh
          xFormat="time:%Y-%m-%d"
          xScale={{
            format: '%Y-%m-%d',
            precision: 'day', // Anthony: this should always be set to day, so that it supports day, week, month, and year properly (regardless of viewBy)
            type: 'time',
            useUTC: false,
            min: xMin,
            max: xMax,
          }}
          yScale={{
            type: 'linear',
            min: yMin,
            max: yMax,
          }}
          data={visibleSeries}
          layers={layers}
          pointLabel={() => ''}
          colors={handleSeriesColor}
          tooltip={(p) => {
            return (
              <NivoSafeTooltipWrapper
                pointX={p.point.x}
                pointY={p.point.y}
                containerRef={containerRef}
                chartMargin={RESPONSIVE_LINE_MARGIN}
              >
                {handleTooltip(p)}
              </NivoSafeTooltipWrapper>
            );
          }}
          theme={{
            text: {
              fontFamily: KARLA_FONT_FAMILY,
              fontSize: 12,
            },
            axis: {
              ticks: {
                text: {
                  fill: '#696D70',
                },
              },
              legend: {
                text: {
                  fill: '#292D33',
                  fontSize: 14,
                  fontWeight: 600,
                },
              },
            },
            grid: {
              line: {
                stroke: '#E5E7EB',
                strokeWidth: '1',
                strokeDasharray: '4 4',
              },
            },
          }}
        />
      </Box>
      <Box
        display="flex"
        flexWrap="wrap"
        flexDirection="row"
        gap="xs"
        justifyContent="center"
      >
        {!isPrintMode && (
          <ChartLegendButton
            label={language.timeSeriesTrendsLineChartLegendAllLabel()}
            id={''}
            focused={hiddenSeries.size === 0}
            onClick={resetSeriesVisibility}
            hasTransparentBorder={false}
          />
        )}
        {allSeries.map((series) => (
          <ChartLegendButton
            key={series.id}
            id={String(series.id)}
            label={language.timeSeriesTrendsLineChartSeriesLabel({
              seriesId: String(series.id),
              metricDefinition,
            })}
            iconColor={ConfigColors[series.color].Primary.bg}
            focused={
              hiddenSeries.size === 0 || isSeriesVisible(String(series.id))
            }
            onClick={toggleSeriesVisibility}
            hasTransparentBorder={isPrintMode}
            isDashed={series.id === AverageTrendSeriesId}
          />
        ))}
      </Box>
    </Spacing>
  );
};

const TimeSeriesSeriesPointTooltip = ({
  bucket,
  compareBucket,
  elementValue,
  elementNumerator,
  compareElementValue,
  compareElementNumerator,
  compareTotal,
  units,
  elementTotal,
  displayLevel,
  metricDefinition,
  isMetricBoolean,
  color,
}: {
  bucket: TimeSeriesTrendsResultBucket;
  compareBucket: TimeSeriesTrendsResultBucket | undefined;
  compareElementValue: number | null;
  elementValue: number | null;
  compareTotal: number | null;
  units: NumericDisplayUnit;
  elementTotal: number | null;
  elementNumerator: number | null;
  compareElementNumerator: number | null;
  displayLevel: string;
  metricDefinition: MyMetricDefinition;
  isMetricBoolean: boolean;
  color: Color;
}) => {
  const deltaValue =
    compareElementValue !== null && elementValue !== null
      ? elementValue - compareElementValue
      : null;

  const formattedDeltaValue =
    deltaValue === null
      ? undefined
      : formatTooltipMetricValue({
          value: deltaValue,
          units,
        });

  return (
    <ProgressPageChartTooltipPure
      displayPeriod={humanizeDateRange({
        start: bucket.start,
        end: bucket.end,
      })}
      color={color}
      compareDisplayCount={
        compareTotal === null ? undefined : String(compareTotal)
      }
      compareDisplayPeriod={
        !compareBucket
          ? undefined
          : humanizeDateRange({
              start: compareBucket.start,
              end: compareBucket.end,
            })
      }
      compareDisplayValue={
        compareElementValue === null
          ? undefined
          : formatTooltipMetricValue({
              value: compareElementValue,
              units,
            })
      }
      deltaDisplayValue={
        deltaValue === null
          ? undefined
          : deltaValue >= 0
            ? `+${formattedDeltaValue}`
            : `-${formattedDeltaValue}`
      }
      displayCount={elementTotal === null ? 'N/A' : String(elementTotal)}
      displayLevel={displayLevel}
      displayValue={
        elementValue === null
          ? 'N/A'
          : formatTooltipMetricValue({
              value: elementValue,
              units,
            })
      }
      metricDisplayName={metricDefinition.displayName}
      isMetricBoolean={isMetricBoolean}
      whiteLabelingSubjectPlural={metricDefinition.whiteLabeling.subjectPlural}
      displayNumerator={
        elementNumerator !== null
          ? formatTooltipCount(elementNumerator)
          : undefined
      }
      compareDisplayNumerator={
        compareElementNumerator !== null
          ? formatTooltipCount(compareElementNumerator)
          : undefined
      }
    />
  );
};

const reduceTotals = (
  elements: {
    total: number | null;
  }[],
): number | null => {
  return elements.reduce((acc: number | null, el) => {
    if (el.total !== null) {
      return (acc ?? 0) + el.total;
    }
    return acc;
  }, null);
};

const reduceAttributeByKey = (
  elements: {
    [key: string]: string | number | boolean | null;
  }[],
  attributeKey: string,
): number | null => {
  return elements.reduce((acc: number | null, el) => {
    if (attributeKey in el && el[attributeKey] !== null) {
      return (acc ?? 0) + Number(el[attributeKey]);
    }
    return acc;
  }, null);
};
